/**
 * React hooks for the Condition Suggestions Admin API (CA-324, Phase 4).
 *
 * Implemented with plain React useState/useEffect to stay consistent with the
 * existing `apiService` usage in the Administration Portal (no React Query
 * installed). The hook API surface mirrors the curated-integrations.api.ts
 * pattern: queries expose `{ data, isLoading, error, refetch }` and mutations
 * expose `{ mutate, mutateAsync, isLoading, error, data }`.
 *
 * Auth is handled transparently by `apiService` (in-memory Bearer token +
 * automatic refresh via httpOnly cookie).
 *
 * Endpoints:
 *   GET/POST/PUT/DELETE /api/admin/condition-suggestions/categories[/:id]
 *   GET/POST/PUT/DELETE /api/admin/condition-suggestions[/:id]
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/api';

// ─── Domain Types ─────────────────────────────────────────────────────────────

/**
 * Discriminator for the two suggestion directive types.
 * Values must stay in sync with `ConditionSuggestionKind` in `@callbert-ai/shared`
 * (app/libs/shared/src/interfaces/condition-suggestion-kind.interface.ts).
 *
 * `PRE_CONDITION` — Gate-condition that controls whether a tool may execute.
 * `BEHAVIOR_INSTRUCTION` — Behavioral rule that guides how a tool executes.
 */
export const ConditionSuggestionKind = {
  PRE_CONDITION: 'PRE_CONDITION',
  BEHAVIOR_INSTRUCTION: 'BEHAVIOR_INSTRUCTION',
} as const;

/** Inferred union type from the `ConditionSuggestionKind` const object. */
export type ConditionSuggestionKind =
  (typeof ConditionSuggestionKind)[keyof typeof ConditionSuggestionKind];

/** Valid tool scope values — must stay in sync with backend DTO. */
export type ToolScope = 'warm_transfer' | 'outcall' | 'all';

/** Response shape for a category as returned by the admin API. */
export interface ConditionSuggestionCategory {
  /** UUID primary key. */
  id: string;
  /** Unique display name shown in the Frontend suggestion picker. */
  name: string;
  /** Optional helper text describing which condition types belong here. */
  description?: string;
  /** Sort order for displaying categories (ascending, lower = first). */
  displayOrder: number;
  /** Number of suggestions attached to this category. */
  suggestionCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** Response shape for a single suggestion entry as returned by the admin API. */
export interface ConditionSuggestion {
  /** UUID primary key. */
  id: string;
  /** UUID of the parent `ConditionSuggestionCategory`. */
  categoryId: string;
  /** Hydrated category object (JOIN in list endpoint). */
  category?: ConditionSuggestionCategory;
  /**
   * The suggestion text shown to the customer.
   * Max 2000 characters; written to `AgentToolFunction.configuration.preCondition.text`
   * when selected by the user.
   */
  text: string;
  /**
   * Which tool type(s) this suggestion applies to.
   * `warm_transfer` = warm-transfer tools only; `outcall` = outcall only; `all` = universal.
   */
  toolScope: ToolScope;
  /** Sort order within the category (ascending, lower = first). */
  displayOrder: number;
  /**
   * Whether this suggestion is visible in the Frontend picker.
   * Inactive suggestions are hidden but retained for audit / re-activation.
   */
  isActive: boolean;
  /**
   * Directive type of this suggestion.
   * `PRE_CONDITION` = Gate-condition (controls whether a tool may execute).
   * `BEHAVIOR_INSTRUCTION` = Behavioral rule (guides how a tool executes).
   * Determines in which Admin list and Customer picker this suggestion appears.
   */
  kind: ConditionSuggestionKind;
  createdAt: string;
  updatedAt: string;
}

/** Filter parameters for `useListSuggestions` and `fetchSuggestions`. */
export interface SuggestionFilter {
  /**
   * Restrict results to one directive type.
   * `PRE_CONDITION` = Gate-conditions only; `BEHAVIOR_INSTRUCTION` = Behavior rules only.
   * When omitted the backend returns suggestions of all kinds.
   */
  kind?: ConditionSuggestionKind;
  toolScope?: ToolScope | 'all';
  categoryId?: string;
  isActive?: boolean;
  search?: string;
}

/** DTO for creating or updating a category. */
export interface CategoryFormData {
  name: string;
  description?: string;
  displayOrder?: number;
}

/**
 * DTO for creating a new suggestion.
 * `kind` is required on create — it determines in which Admin list and
 * Customer picker the suggestion will appear and cannot be changed after creation.
 */
export interface CreateSuggestionData {
  categoryId: string;
  text: string;
  toolScope: ToolScope;
  /**
   * Directive type for this suggestion.
   * Must be set at creation time; read-only afterwards (see `UpdateSuggestionData`).
   */
  kind: ConditionSuggestionKind;
  displayOrder?: number;
  isActive?: boolean;
}

/**
 * DTO for partially updating an existing suggestion.
 * `kind` is optional — when provided it allows reclassifying the suggestion
 * (e.g. PRE_CONDITION → BEHAVIOR_INSTRUCTION). Most callers omit it.
 *
 * @deprecated Avoid mutating `kind` on existing suggestions if Customers may
 * have already selected them; prefer creating a new suggestion of the target kind.
 */
export interface UpdateSuggestionData {
  categoryId?: string;
  text?: string;
  toolScope?: ToolScope;
  /** Optional reclassification of the directive type. See JSDoc on this interface for caveats. */
  kind?: ConditionSuggestionKind;
  displayOrder?: number;
  isActive?: boolean;
}

/**
 * @deprecated Use `CreateSuggestionData` for creating and `UpdateSuggestionData`
 * for updating suggestions. This alias is kept temporarily for backward compatibility
 * with callers that used the old combined type.
 */
export type SuggestionFormData = CreateSuggestionData;

// ─── Shared Query / Mutation Result Types ─────────────────────────────────────

/** Shape returned by all query hooks. */
export interface QueryResult<TData> {
  /** Fetched data, undefined while loading or on error. */
  data: TData | undefined;
  /** True during the initial fetch and on manual refetch. */
  isLoading: boolean;
  /** Error thrown by the last fetch attempt, or null. */
  error: Error | null;
  /** Manually trigger a refetch. */
  refetch: () => void;
}

/** Shape returned by all mutation hooks. */
export interface MutationResult<TData, TVariables> {
  /**
   * Call the mutation.
   * Swallows errors — check `error` state afterwards.
   */
  mutate: (variables: TVariables) => void;
  /**
   * Call the mutation and return a Promise.
   * Throws on error — use try/catch.
   */
  mutateAsync: (variables: TVariables) => Promise<TData>;
  /** True while the mutation request is in-flight. */
  isLoading: boolean;
  /** Last successful response data, or undefined. */
  data: TData | undefined;
  /** Error from the last mutation attempt, or null. */
  error: Error | null;
}

// ─── Internal hook factories ──────────────────────────────────────────────────

function useQuery<TData>(
  fetcher: () => Promise<TData>,
  deps: unknown[],
): QueryResult<TData> {
  const [data, setData] = useState<TData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchIndex, setRefetchIndex] = useState(0);

  const refetch = useCallback(() => setRefetchIndex((i) => i + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setIsLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refetchIndex]);

  return { data, isLoading, error, refetch };
}

function useMutation<TData, TVariables>(
  mutator: (variables: TVariables) => Promise<TData>,
): MutationResult<TData, TVariables> {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TData | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);

  const mutatorRef = useRef(mutator);
  mutatorRef.current = mutator;

  const mutateAsync = useCallback(async (variables: TVariables): Promise<TData> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await mutatorRef.current(variables);
      setData(result);
      return result;
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      throw wrapped;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const mutate = useCallback(
    (variables: TVariables) => {
      mutateAsync(variables).catch(() => {
        // Error stored in `error` state; caller can check it
      });
    },
    [mutateAsync],
  );

  return { mutate, mutateAsync, isLoading, data, error };
}

// ─── Raw API functions (called by hooks + direct imports) ─────────────────────

const BASE = '/api/admin/condition-suggestions';

/**
 * Fetch all categories.
 * GET /api/admin/condition-suggestions/categories
 */
export async function fetchCategories(): Promise<ConditionSuggestionCategory[]> {
  return apiService['request']<ConditionSuggestionCategory[]>(`${BASE}/categories`);
}

/**
 * Create a new category.
 * POST /api/admin/condition-suggestions/categories
 */
export async function createCategory(dto: CategoryFormData): Promise<ConditionSuggestionCategory> {
  return apiService['request']<ConditionSuggestionCategory>(`${BASE}/categories`, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

/**
 * Update an existing category.
 * PUT /api/admin/condition-suggestions/categories/:id
 */
export async function updateCategory(
  id: string,
  dto: Partial<CategoryFormData>,
): Promise<ConditionSuggestionCategory> {
  return apiService['request']<ConditionSuggestionCategory>(`${BASE}/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

/**
 * Delete a category.
 * Returns 409 Conflict when suggestions still reference this category.
 * DELETE /api/admin/condition-suggestions/categories/:id
 */
export async function deleteCategory(id: string): Promise<void> {
  return apiService['request']<void>(`${BASE}/categories/${id}`, { method: 'DELETE' });
}

/**
 * Fetch suggestions with optional filters.
 * GET /api/admin/condition-suggestions
 *
 * @param filter - Optional filter criteria.
 * @param filter.kind - Restrict to one directive type (`PRE_CONDITION` or
 *   `BEHAVIOR_INSTRUCTION`). When omitted the backend returns all kinds.
 * @param filter.toolScope - Restrict to one tool scope. `all` is omitted from
 *   the query (backend default).
 * @param filter.categoryId - Restrict to one category UUID.
 * @param filter.isActive - Filter by active/inactive status.
 * @param filter.search - Free-text search across suggestion text.
 */
export async function fetchSuggestions(filter?: SuggestionFilter): Promise<ConditionSuggestion[]> {
  const params = new URLSearchParams();
  if (filter?.kind) params.set('kind', filter.kind);
  if (filter?.toolScope && filter.toolScope !== 'all') params.set('toolScope', filter.toolScope);
  if (filter?.categoryId) params.set('categoryId', filter.categoryId);
  if (filter?.isActive !== undefined) params.set('isActive', String(filter.isActive));
  if (filter?.search) params.set('search', filter.search);
  const qs = params.toString();
  return apiService['request']<ConditionSuggestion[]>(`${BASE}${qs ? `?${qs}` : ''}`);
}

/**
 * Create a new suggestion.
 * POST /api/admin/condition-suggestions
 *
 * @param dto - Creation payload. `kind` is required — it determines the
 *   directive type (PRE_CONDITION or BEHAVIOR_INSTRUCTION) and cannot be
 *   changed without using `updateSuggestion`.
 */
export async function createSuggestion(dto: CreateSuggestionData): Promise<ConditionSuggestion> {
  return apiService['request']<ConditionSuggestion>(BASE, {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

/**
 * Update an existing suggestion.
 * PUT /api/admin/condition-suggestions/:id
 *
 * @param id - UUID of the suggestion to update.
 * @param dto - Partial update payload. `kind` is optional; when provided it
 *   reclassifies the directive type — use with caution if customers have
 *   already selected this suggestion.
 */
export async function updateSuggestion(
  id: string,
  dto: UpdateSuggestionData,
): Promise<ConditionSuggestion> {
  return apiService['request']<ConditionSuggestion>(`${BASE}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

/**
 * Delete a suggestion.
 * DELETE /api/admin/condition-suggestions/:id
 */
export async function deleteSuggestion(id: string): Promise<void> {
  return apiService['request']<void>(`${BASE}/${id}`, { method: 'DELETE' });
}

// ─── Query Hooks ──────────────────────────────────────────────────────────────

/**
 * Fetches all categories.
 * Endpoint: GET /api/admin/condition-suggestions/categories
 */
export function useListCategories(): QueryResult<ConditionSuggestionCategory[]> {
  return useQuery(() => fetchCategories(), []);
}

/**
 * Fetches suggestions with optional filter params.
 * Re-fetches when any filter field changes (including `kind`).
 * Endpoint: GET /api/admin/condition-suggestions
 *
 * @param filter.kind - When provided, restricts results to the given directive
 *   type (`PRE_CONDITION` or `BEHAVIOR_INSTRUCTION`).
 */
export function useListSuggestions(filter?: SuggestionFilter): QueryResult<ConditionSuggestion[]> {
  return useQuery(
    () => fetchSuggestions(filter),
    [filter?.kind, filter?.toolScope, filter?.categoryId, filter?.isActive, filter?.search],
  );
}

// ─── Mutation Hooks ───────────────────────────────────────────────────────────

/** Creates a new category. POST /api/admin/condition-suggestions/categories */
export function useCreateCategory(): MutationResult<ConditionSuggestionCategory, CategoryFormData> {
  return useMutation((dto) => createCategory(dto));
}

/** Updates an existing category. PUT /api/admin/condition-suggestions/categories/:id */
export function useUpdateCategory(): MutationResult<
  ConditionSuggestionCategory,
  { id: string; dto: Partial<CategoryFormData> }
> {
  return useMutation(({ id, dto }) => updateCategory(id, dto));
}

/** Deletes a category. DELETE /api/admin/condition-suggestions/categories/:id */
export function useDeleteCategory(): MutationResult<void, string> {
  return useMutation((id) => deleteCategory(id));
}

/**
 * Creates a new suggestion. POST /api/admin/condition-suggestions
 *
 * `kind` is required in the mutation variables — it determines the directive
 * type of the new suggestion and is immutable in the Customer picker once set.
 */
export function useCreateSuggestion(): MutationResult<ConditionSuggestion, CreateSuggestionData> {
  return useMutation((dto) => createSuggestion(dto));
}

/**
 * Updates an existing suggestion. PUT /api/admin/condition-suggestions/:id
 *
 * `kind` is optional in `dto` — only provide it when intentionally
 * reclassifying the directive type of the suggestion.
 */
export function useUpdateSuggestion(): MutationResult<
  ConditionSuggestion,
  { id: string; dto: UpdateSuggestionData }
> {
  return useMutation(({ id, dto }) => updateSuggestion(id, dto));
}

/** Deletes a suggestion. DELETE /api/admin/condition-suggestions/:id */
export function useDeleteSuggestion(): MutationResult<void, string> {
  return useMutation((id) => deleteSuggestion(id));
}
